import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

interface Lead {
  _id: string
  nome: string
  email: string
  whatsapp: string
  terraScore: number
  aguaScore: number
  fogoScore: number
  arScore: number
  resultadoFinal: string
  interesseLeituraVibracional: string
  interesseProtocoloLiberacao?: string
  status: string
  createdAt: string
  updatedAt: string
}

export const useLeads = () => {
  const [leads, setLeads] = useState<Lead[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLeads = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    setError(null)
    
    try {
      const { list, total } = await lumi.entities.leads.list({
        filter: options?.filter || {},
        sort: options?.sort || { createdAt: -1 },
        limit: options?.limit || 100,
        skip: options?.skip || 0
      })
      
      setLeads(list as Lead[])
      setTotal(total)
      console.log('Leads carregados:', list.length)
    } catch (err: any) {
      console.error('Erro ao carregar leads:', err)
      setError(err.message || 'Erro ao carregar leads')
    } finally {
      setLoading(false)
    }
  }

  const saveLead = async (leadData: {
    nome: string
    email: string
    whatsapp: string
    terraScore: number
    aguaScore: number
    fogoScore: number
    arScore: number
    terraPercentual: number
    aguaPercentual: number
    fogoPercentual: number
    arPercentual: number
    terraClassificacao: string
    aguaClassificacao: string
    fogoClassificacao: string
    arClassificacao: string
    elementosMaiorBloqueio: string[]
    interesseLeituraVibracional: string
    interesseProtocoloLiberacao?: string
  }) => {
    try {
      const now = new Date().toISOString()
      
      const newLead = await lumi.entities.leads.create({
        ...leadData,
        status: 'Novo',
        creator: 'system',
        createdAt: now,
        updatedAt: now
      })
      
      console.log('Lead salvo com sucesso:', newLead)
      return newLead
    } catch (err: any) {
      console.error('Erro ao salvar lead:', err)
      throw err
    }
  }

  const searchLeads = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      await fetchLeads()
      return
    }

    await fetchLeads({
      filter: {
        $or: [
          { nome: { $regex: searchTerm, $options: 'i' } },
          { email: { $regex: searchTerm, $options: 'i' } },
          { whatsapp: { $regex: searchTerm, $options: 'i' } }
        ]
      }
    })
  }

  const filterByDateRange = async (startDate: string, endDate: string) => {
    await fetchLeads({
      filter: {
        createdAt: {
          $gte: startDate,
          $lte: endDate
        }
      }
    })
  }

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      await lumi.entities.leads.update(leadId, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      
      console.log('Status atualizado:', leadId, newStatus)
      await fetchLeads()
    } catch (err: any) {
      console.error('Erro ao atualizar status:', err)
      throw err
    }
  }

  const deleteLead = async (leadId: string) => {
    try {
      await lumi.entities.leads.delete(leadId)
      console.log('Lead deletado:', leadId)
      await fetchLeads()
    } catch (err: any) {
      console.error('Erro ao deletar lead:', err)
      throw err
    }
  }

  return {
    leads,
    total,
    loading,
    error,
    fetchLeads,
    saveLead,
    searchLeads,
    filterByDateRange,
    updateLeadStatus,
    deleteLead
  }
}
